import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { getFilterCount, getFiltersByContext } from './selectors';
import { resetFilters, setFilterConfig, setFilters } from './slice';
import {
  FilterData,
  FormVersion,
  UseFiltersOptions,
  UseFiltersReturn,
} from './types';

/**
 * Generic hook for filter persistence and management
 * Handles all Redux interactions for filters
 * Modules provide their own filterFields, metaData, and callbacks
 */
export function useFilters({
  context,
  filterFields,
  metaData,
  formVersion = FormVersion.webRevyre,
  onApply,
  onReset,
}: UseFiltersOptions): UseFiltersReturn {
  const dispatch = useAppDispatch();

  // Store the config in filters model for persistence
  useEffect(() => {
    if (filterFields && filterFields.length > 0) {
      dispatch(
        setFilterConfig({
          context,
          config: {
            context,
            fields: filterFields,
            metaData,
            formVersion,
          },
        }),
      );
    }
  }, [dispatch, context, filterFields, metaData, formVersion]);

  // Read filters and filterCount from Redux store
  const filters = useAppSelector((state) =>
    getFiltersByContext(state, context),
  );
  const filterCount = useAppSelector((state) => getFilterCount(state, context));

  // Handle apply - persists filters and calls module callback
  const handleApply = useMemo(
    () => (appliedFilters: FilterData) => {
      // Persist filters in Redux store
      dispatch(setFilters({ context, filters: appliedFilters }));

      // Call module-specific callback (e.g., fetchEntityList)
      if (onApply) {
        onApply(appliedFilters);
      }
    },
    [dispatch, context, onApply],
  );

  // Handle reset - clears filters and calls module callback
  const handleReset = useMemo(
    () => () => {
      // Clear filters in Redux store
      dispatch(resetFilters({ context }));

      // Call module-specific callback (e.g., fetchEntityList with empty filters)
      if (onReset) {
        onReset();
      }
    },
    [dispatch, context, onReset],
  );

  return {
    filters,
    filterCount,
    handleApply,
    handleReset,
  };
}
